/**
 * Adapter: WHB API `/broker-accounts` row → PWA `BrokerAccount`.
 *
 * The real endpoint only returns identity + metadata — no balances,
 * no curves, no risk metrics. Those must come from per-account
 * detail endpoints we haven't wired yet. The adapter fills required
 * `BrokerAccount` fields with zero / empty-array placeholders and
 * stamps `source: "real"` so views can visibly mark the values as
 * pending instead of silently rendering zeros.
 *
 * Keep this file as the ONE place that decides "what a real account
 * looks like to the UI". When a balances endpoint lands, extend the
 * adapter (and its input type) rather than scattering inference logic
 * across the page tree.
 */
import { buildAccountRiskData, buildRiskDataForTWR } from "@/data/mock-data";
import type {
  Bot,
  BrokerAccount,
  Cashflow,
  RiskMetrics,
  SpyPricePoint,
} from "@/data/mock-data";
import type { Position } from "@/data/mock-positions";
import type { Order, OrderSide, OrderStatus, OrderType } from "@/data/mock-orders";
import type { Activity } from "@/data/mock-activities";
import {
  reconstructEquityCurveWithStats,
  type HistoricalQuotesMap,
  type HistoricalSplitsMap,
} from "@/lib/equity-reconstruction";
import { upsertSgovGapCorrection } from "@/lib/whb-corrections";
import { isTradingDay } from "@/lib/trading-calendar";
import { resolveAssetName } from "@/data/ticker-names";

/** Deterministic 32-bit hash of a string — used to seed the SPY walk
 *  in {@link mapRealBrokerAccountDetail} so the same account id always
 *  produces the same synthetic SPY series across re-renders. Same
 *  distribution spread as Math.imul-based FNV; collisions don't matter
 *  beyond "two accounts happen to share identical SPY shapes". */
function hashStringToSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h | 0;
}

/**
 * Shape observed in production on 2026-04-20 (full 23-row sample on
 * 2026-04-21). Unknown fields are modelled optionally so future API
 * additions don't require a breaking adapter change.
 *
 * `providerType` and `accountType` are numeric enums. The mapping below
 * was reverse-engineered from the full sample (every displayName ends
 * in a parenthesised "<vendor> <kind>" tag that lines up with the two
 * numbers).
 *
 *   providerType:
 *     1 → Alpaca           (displayName ends "(Alpaca Live)" / "(Alpaca Paper)")
 *     2 → WhiteHatBots     (displayName ends "(WhiteHatBots )" / "(WhiteHatBots Paper)")
 *
 *   accountType:
 *     0 → WHB-native sim   (providerType=2 only; backtest / marketplace scratch accounts,
 *                           not real paper trading at a broker)
 *     3 → Paper            (broker paper trading — both Alpaca Paper and WHB Paper)
 *     4 → Live             (real money — only Alpaca Live observed)
 *
 * The "is live" test for the UI is `accountType === 4`. Everything else
 * (paper, sim, missing) is treated as non-live.
 */
export const ACCOUNT_TYPE = {
  /** WHB-native simulation / backtest scratch account. */
  Sim: 0,
  /** Broker paper trading account. */
  Paper: 3,
  /** Real-money live account. */
  Live: 4,
} as const;

export const PROVIDER_TYPE = {
  Alpaca: 1,
  WhiteHatBots: 2,
} as const;

export interface RawBrokerAccount {
  id: string;
  name?: string;
  displayName?: string;
  externalId?: string;
  providerType?: number;
  accountType?: number;
  botsCount?: number;
  [extra: string]: unknown;
}

/**
 * Shape observed on 2026-04-21 for GET `/broker-accounts/{id}`. This is
 * the single "account detail" call — the one the real app fires when
 * you drill into an account on the landing page. It returns everything
 * needed for the Accounts hero in one round trip:
 *
 *   • `portfolio`: hero numbers (equity, buyingPower, cash, stocks…)
 *   • `states`:    per-day snapshots — the equity curve. account-level
 *                  rows have `botId === null`; bot-level rows carry a
 *                  `botId`. Newest-first from the server.
 *   • `stocks`:    current open positions.
 *   • `bots`:      bot summary cards for the account.
 *
 * Full field-by-field notes live in `docs/WHB-API-RECON.md`.
 */
export interface RawAccountPortfolio {
  equity?: number;
  totalProfit?: number;
  totalProfitPercent?: number;
  buyingPower?: number;
  cash?: number;
  margin?: number;
  marginAvailable?: number;
  marginUsed?: number;
  stocksValue?: number;
  stocksPurchaseValue?: number;
  stocksProfitPercents?: number;
}

export interface RawAccountState {
  id?: string;
  brokerAccountId?: string;
  timestamp?: string;
  /** `null` on account-level rows (the equity curve). Non-null rows are
   *  per-bot snapshots we currently ignore for the hero curve. */
  botId?: string | null;
  cash?: number;
  margin?: number;
  stocks?: number;
}

export interface RawAccountStock {
  symbol?: string;
  company?: string;
  qty?: number;
  avgFillPrice?: number;
  currentUnitPrice?: number;
  /** % change since fill — signed. The API serialises it already
   *  multiplied by 100 (e.g. `-17.61` means -17.61%). */
  valueChange?: number;
  /** Backslash-separated WHB CDN URL — not usable directly in <img>
   *  in all browsers. The PWA prefers its Parqet fallback. */
  icon?: string;
}

export interface RawAccountBot {
  id?: string;
  name?: string;
  icon?: string;
  status?: number;
  /** Current $ value of the bot's slice of the portfolio. */
  value?: number;
}

export interface RawBrokerAccountDetail extends RawBrokerAccount {
  portfolio?: RawAccountPortfolio;
  states?: RawAccountState[];
  stocks?: RawAccountStock[];
  bots?: RawAccountBot[];
}

/**
 * Row shape of `GET /broker-accounts/{id}/financial-activities`.
 *
 * The endpoint returns a flat array sorted ascending by `timestamp`
 * (oldest → newest). Distinct `name` values observed so far:
 *
 *   • `CashDeposit`       — user-initiated cash deposit (positive).
 *   • `CashWithdrawal`    — user-initiated cash withdrawal (negative).
 *                           Per Sergey — "CashWithdrawal represents
 *                           Withdrawal". None present on the probed
 *                           Ellen Live account; shape inferred.
 *   • `JournalEntryCash`  — broker-side cash journal (treated as a
 *                           deposit per Sergey's note).
 *   • `Dividend`          — dividend payment (positive, or negative on
 *                           corrections — see below).
 *   • `Interest`          — cash sweep interest earned (positive) OR
 *                           margin interest charged (negative).
 *   • `Fee`               — clearing/regulatory fees (always negative,
 *                           usually -$0.01).
 *
 * Correction pattern: same `timestamp` can carry pairs of equal-and-
 * opposite rows with unique `id`s (broker posts a reversal + re-post).
 * Sum by amount; never de-dupe by timestamp.
 *
 * Full recon in `docs/WHB-API-RECON.md` §5.
 */
export interface RawAccountActivity {
  id: string;
  timestamp: string;
  name: string;
  amount: number;
}

/** Names that represent user-controlled cashflows (move ND needle and
 *  render as deposit/withdrawal dots on the $ chart).
 *
 *  - Dividend / Interest / Fee are **returns**, not deposits, and are
 *    deliberately excluded.
 *  - JournalEntryCash is a **broker-side** internal cash journal
 *    (e.g. Alpaca posting a rounding credit, a reversal pair, or a
 *    sweep-to-margin), not a user-initiated transfer. Per Sergey
 *    2026-04-23: "March 29 $11 withdrawal is not cashflow as this is
 *    not actual account transfer". Excluded from dots. The raw
 *    activity still flows through the reconstruction's cash math
 *    (see the full-activities map in mapRealBrokerAccountDetail) so
 *    cash totals remain accurate; we only keep it out of the
 *    user-visible deposit/withdrawal surface.
 */
const USER_CASHFLOW_NAMES = new Set([
  "CashDeposit",
  "CashWithdrawal",
]);

/**
 * Tickers that WHB's `/trading-history` endpoint FILTERS OUT of the
 * orders feed, but which DO have current holdings surfaced in
 * `/broker-accounts/{id}.stocks[]`. These are money-market /
 * short-term-treasury ETFs that the WHB backend treats as cash-
 * management instruments outside the normal bot trading stream.
 *
 * Because we can't reconstruct their lifetime cash impact (no orders
 * feed), we treat the CURRENT position market value as if it were
 * part of cash — a "cash equivalent" position. This closes the cash
 * accounting gap without needing every historical trade:
 *
 *   true cash delta from lifetime SGOV trades
 *     = (qty held today × current price) − 0        // since 0 shares initially
 *     = current market value of the SGOV position
 *
 * Approximation: this ignores the yield earned vs cash rate
 * differential, which for SGOV is ~5% APY vs ~4% cash-sweep ≈ <1% of
 * the held value per year. Acceptable for real-money reporting
 * accuracy until WHB fixes the /trading-history filter.
 *
 * Investigation 2026-04-23: WHB filters 15 SGOV trades for Default
 * Live and 10 for Ellen Live, causing $64.80 and $101.46 cash drift
 * respectively. Treating the current SGOV position as cash closes
 * both gaps. See `project_whb_trading_history_bugs` memory.
 *
 * If we later identify other filtered symbols (MINT, BIL, ICSH, VUSB,
 * etc.), add them here — same semantics apply.
 */
const CASH_EQUIVALENT_SYMBOLS = new Set(["SGOV"]);

function isCashEquivalent(symbol: string | undefined | null): boolean {
  return typeof symbol === "string" && CASH_EQUIVALENT_SYMBOLS.has(symbol.toUpperCase());
}

/**
 * Generate a daily list of `YYYY-MM-DD` strings from `startDate`
 * (inclusive) up to `endDateExclusive`. Used by
 * {@link reconstructPreStateCurve} to produce a dense date axis for
 * the pre-state backfill region.
 *
 * Date math runs in UTC to match the API's `states[].timestamp` shape
 * (midnight-UTC-floored) and avoid off-by-one drift around DST.
 */
function dailyDateRange(startDate: string, endDateExclusive: string): string[] {
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDateExclusive}T00:00:00Z`);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  if (start >= end) return [];
  const out: string[] = [];
  const cur = new Date(start);
  while (cur < end) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

/**
 * Reconstruct a **pre-state equity curve** from cashflows alone.
 *
 * Why this exists
 * ---------------
 * WHB's `states[]` daily snapshots typically start later than the
 * account's first activity — Default Live's snapshots begin ~2025-09
 * but its first `CashDeposit` was 2024-10-30. Without reconstruction
 * the ALL range would begin in 2025-09, hiding nearly a year of
 * account history that the user sees on the real WHB web app. Sergey
 * explicitly asked for "all graphs [$] and [%] since first Activity".
 *
 * Model
 * -----
 * For the pre-state window we don't have daily equity snapshots, and
 * we can't mark open positions to market on arbitrary past dates
 * without historical per-ticker price feeds (which WHB doesn't
 * surface). The pragmatic, truthful approximation is:
 *
 *   equity[t] = Σ cashflow[i].amount  for every cf with cf.date ≤ t
 *
 * i.e. "cumulative net deposits, no market return assumed". The chart
 * will show a cashflow-dot staircase across the backfilled period
 * and then transition to real market-driven movement on the first
 * state day. Any gap between the last pre-state equity point and the
 * first state's equity is the **cumulative market P&L** during the
 * unobserved window — a one-day jump that visually marks "real daily
 * data begins here". We deliberately don't smooth that jump with a
 * SPY-tracking heuristic because that would lie about the actual
 * trajectory.
 *
 * Inputs
 * ------
 *   • `cashflows`        — sorted ascending by `date` (normaliser's
 *                          contract). Only cashflows strictly before
 *                          `firstStateDate` are used.
 *   • `firstStateDate`   — ISO yyyy-mm-dd of the earliest state
 *                          snapshot. Daily backfill runs up to, but
 *                          not including, this date so the state
 *                          day itself isn't duplicated.
 *
 * Returns `null` when no backfill is needed — either no cashflows
 * pre-date the state window, or the state window itself is empty.
 * Callers should leave the existing equityCurve/curveDates alone in
 * that case.
 */
export function reconstructPreStateCurve(
  cashflows: readonly Cashflow[] | null | undefined,
  firstStateDate: string | undefined
): { dates: string[]; equity: number[] } | null {
  if (!cashflows || cashflows.length === 0 || !firstStateDate) return null;
  // Only cashflows strictly earlier than the first snapshot fill a
  // gap. Cashflows on or after firstStateDate are already reflected
  // in `states[].cash`.
  const preState = cashflows.filter((cf) => cf.date < firstStateDate);
  if (preState.length === 0) return null;
  const startDate = preState[0]!.date;
  const dates = dailyDateRange(startDate, firstStateDate);
  if (dates.length === 0) return null;

  // Walk day by day accumulating cashflows. `cfIdx` advances
  // monotonically so the outer loop is linear in the number of days.
  const equity: number[] = [];
  let cumulative = 0;
  let cfIdx = 0;
  for (const date of dates) {
    while (cfIdx < preState.length && preState[cfIdx]!.date <= date) {
      cumulative += preState[cfIdx]!.amount;
      cfIdx++;
    }
    equity.push(+cumulative.toFixed(2));
  }
  return { dates, equity };
}

/**
 * Compute a cashflow-adjusted **time-weighted return** curve (cumulative
 * %, rebased to 0 at start) from an equity series and its cashflow
 * events.
 *
 * Why this exists
 * ---------------
 * The naive price-ratio TWR (`(equity[i] / equity[0]) − 1`) double-
 * counts user deposits and withdrawals: if a user deposits $10k mid-
 * period, the equity curve jumps and the naive formula reports a
 * fake +return equal to the deposit. Proper TWR neutralizes that by
 * chain-linking per-period returns computed between cashflow events.
 *
 * Algorithm (standard industry formula)
 * -------------------------------------
 * For each daily step i = 1…n:
 *   cf[i]          = sum of cashflows dated curveDates[i]
 *   periodReturn   = (equity[i] − cf[i] − equity[i-1]) / equity[i-1]
 *   cumReturn[i]   = cumReturn[i-1] × (1 + periodReturn) − 1
 *
 * The cashflow is subtracted from the ENDING equity before the
 * period-return divide because the broker reports equity[i] including
 * any deposits that landed on day i; we want to isolate the market-
 * driven component.
 *
 * Safety
 * ------
 * • Zero / negative start equity on a given day yields a 0% period
 *   return (instead of dividing by ~0 and exploding into NaN).
 * • Missing equity values default to 0; the chain is resilient to a
 *   single bad sample but a long stretch of zeros would flatten the
 *   curve, which is the correct UX failure mode.
 */
export function computeChainLinkedTWR(
  equityCurve: readonly number[],
  curveDates: readonly string[],
  cashflows: readonly Cashflow[] | null | undefined
): number[] {
  const n = equityCurve.length;
  if (n === 0) return [];

  // Index cashflows by day for O(1) lookup during the chain walk.
  // Dates align on YYYY-MM-DD (per `activitiesToCashflows` normalizer).
  const cfByDate = new Map<string, number>();
  if (cashflows) {
    for (const cf of cashflows) {
      if (!cf) continue;
      const d = cf.date;
      const prev = cfByDate.get(d) ?? 0;
      cfByDate.set(d, prev + cf.amount);
    }
  }

  // Seed the curve at 0% and step forward one day at a time.
  const twr: number[] = [0];
  let cum = 1; // product of (1 + periodReturn) factors
  for (let i = 1; i < n; i++) {
    const start = equityCurve[i - 1] ?? 0;
    const end = equityCurve[i] ?? 0;
    const date = curveDates[i] ?? "";
    const cf = cfByDate.get(date) ?? 0;
    // Guard against divide-by-zero on day 0 of a brand-new account
    // (equity starts at 0 until the opening deposit lands).
    const periodReturn = start > 0 ? (end - cf - start) / start : 0;
    cum *= 1 + periodReturn;
    twr.push(+((cum - 1) * 100).toFixed(3));
  }
  return twr;
}

/**
 * Convert a raw activities array into the PWA's `Cashflow[]` shape.
 *
 * Only user cashflow rows survive — Dividend / Interest / Fee are
 * filtered out because they're returns, not deposits, and would
 * corrupt Net Deposits if included. Rows with the same timestamp
 * (e.g. the Feb 4 2025 Interest cluster's reversal pairs) are kept
 * distinct — we never sum by date here, only classify by sign.
 *
 * Output is sorted ascending by date to match `curveDates`.
 */
export function activitiesToCashflows(
  activities: readonly RawAccountActivity[] | null | undefined
): Cashflow[] {
  if (!Array.isArray(activities)) return [];
  return activities
    .filter((a) => a && USER_CASHFLOW_NAMES.has(a.name))
    .filter((a) => typeof a.amount === "number" && isFinite(a.amount))
    .map<Cashflow>((a) => ({
      date: String(a.timestamp).slice(0, 10),
      amount: a.amount,
      // Sign drives the type, not the name — a negative JournalEntryCash
      // reads as a withdrawal in the UI, which is what the colour dots
      // encode.
      type: a.amount >= 0 ? "deposit" : "withdrawal",
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Convert raw WHB `/financial-activities` rows into the PWA's `Activity[]`
 * shape for the Activities tab. Each WHB `name` maps to one of the five
 * {@link Activity} type slots:
 *
 *   CashDeposit        → transfer (in)
 *   CashWithdrawal     → transfer (out)
 *   JournalEntryCash   → transfer (sign-driven direction) — broker-side
 *                        journal; shown so the user sees it exists but
 *                        labeled clearly in the description.
 *   Dividend           → dividend
 *   Interest           → interest
 *   Fee                → fee
 *
 * Trades (buys/sells) are NOT in the /financial-activities feed —
 * they live in `/trading-history`. A future enhancement can merge
 * orders into this list so the Activities tab shows everything in one
 * place. For now trades appear on the Orders tab only.
 */
export function mapRealActivitiesToActivities(
  activities: readonly RawAccountActivity[] | null | undefined,
  accountId: string
): Activity[] {
  if (!Array.isArray(activities)) return [];
  const out: Activity[] = [];
  for (const a of activities) {
    if (!a || typeof a.amount !== "number" || !isFinite(a.amount)) continue;
    const amount = a.amount;
    const ts = String(a.timestamp);
    let type: Activity["type"];
    let description: string;
    let transferDirection: Activity["transferDirection"] | undefined;
    switch (a.name) {
      case "CashDeposit":
        type = "transfer";
        transferDirection = "in";
        description = "ACH deposit";
        break;
      case "CashWithdrawal":
        type = "transfer";
        transferDirection = "out";
        description = "ACH withdrawal";
        break;
      case "JournalEntryCash":
        type = "transfer";
        transferDirection = amount >= 0 ? "in" : "out";
        description = "Broker journal entry";
        break;
      case "Dividend":
        type = "dividend";
        description = amount >= 0 ? "Dividend received" : "Dividend correction";
        break;
      case "Interest":
        type = "interest";
        description = amount >= 0 ? "Interest earned" : "Margin interest charged";
        break;
      case "Fee":
        type = "fee";
        description = "Regulatory / clearing fee";
        break;
      default:
        // Unknown activity name — surface it verbatim under the
        // closest bucket so the user can still see it.
        type = "fee";
        description = String(a.name ?? "Unknown");
        break;
    }
    out.push({
      id: a.id,
      accountId,
      timestamp: ts,
      type,
      status: "settled",
      amount,
      description,
      ...(transferDirection ? { transferDirection } : {}),
    });
  }
  // Newest-first matches ActivitiesSimpleView's grouping expectation.
  out.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  return out;
}

/**
 * Convert the `stocks[]` array from a `/broker-accounts/{id}` detail
 * response into the PWA's `Position[]` display shape.
 *
 * Mapping notes:
 *   • `symbol`            → `asset`
 *   • `company`           → `assetName`
 *   • `qty`               → `qty`                    (always positive — Alpaca
 *                                                    long-only for the accounts
 *                                                    we've seen)
 *   • `avgFillPrice`      → `avgEntryPrice`
 *   • `currentUnitPrice`  → `currentPrice`
 *   • `valueChange`       → `totalPLPct`              (broker reports as
 *                                                    percentage like -3.64;
 *                                                    we divide by 100 to
 *                                                    store a *fraction*
 *                                                    matching the convention
 *                                                    used by mock positions
 *                                                    + every formatter on
 *                                                    the page — without
 *                                                    that, formatPctSigned
 *                                                    would multiply ×100 a
 *                                                    second time and report
 *                                                    a 100× inflated %)
 *   • `qty × currentUnitPrice`      → `marketValue`
 *   • `qty × avgFillPrice`          → `costBasis`
 *   • `(currentUnitPrice − avgFillPrice) × qty` → `totalPLDollar`
 *
 * Fields we can't fill from the detail response (left as safe defaults):
 *   • `side`              — always `"long"` until we've seen a short in the
 *                           API; Alpaca shows short qty as negative but this
 *                           account's stocks are all long.
 *   • `assetClass`        — `"stocks"` (no classifier surfaced by the API).
 *   • `botId`             — unknown. The detail response doesn't attribute
 *                           positions to a bot. Leave undefined.
 *   • `dayChangePct`, `todayPLDollar`, `todayPLPct` — no per-day delta in
 *                           the detail response; the chart-level daily
 *                           change covers the aggregate but per-position
 *                           is not available, so leave at 0.
 *   • `sparkline`         — no per-position price history; empty array.
 *   • `openedAt`          — derived from the earliest BUY trade for this
 *                           symbol in the supplied trading-history rows.
 *                           Empty string when no orders are passed in
 *                           (cache-first paint) or no matching buy is
 *                           found.
 *
 * Rows with no `symbol` are skipped — a positionless row is meaningless.
 */
export function mapRealStocksToPositions(
  stocks: readonly RawAccountStock[] | null | undefined,
  accountId: string,
  orders?: readonly RawTradingHistoryRow[] | null
): Position[] {
  // Build a symbol → earliest-buy-date map from the trading-history
  // feed. Sergey 2026-05-01: positions should show when each holding
  // was first opened so the drawer's "Opened" field stops reading
  // "—". The earliest BUY is the inception of the running lot; sells
  // since don't reset the open date (the position has been "open"
  // since that first buy, regardless of intermediate trims). Linear
  // scan because orders are small per account.
  const earliestBuyByTicker = new Map<string, string>();
  if (Array.isArray(orders)) {
    for (const o of orders) {
      if (!o || o.action !== 1) continue; // 1 = Buy; ignore sells
      const sym = typeof o.symbol === "string" ? o.symbol : null;
      const date = typeof o.date === "string" ? o.date : null;
      if (!sym || !date) continue;
      const prev = earliestBuyByTicker.get(sym);
      if (!prev || date < prev) earliestBuyByTicker.set(sym, date);
    }
  }
  if (!Array.isArray(stocks)) return [];
  const out: Position[] = [];
  for (const s of stocks) {
    if (!s || typeof s.symbol !== "string" || !s.symbol) continue;
    // Hide cash-equivalent ETFs (SGOV, etc.) from the Positions UI —
    // they are surfaced as cash in the hero + Balances panel, not as
    // stock positions, to match Alpaca's treatment.
    if (isCashEquivalent(s.symbol)) continue;
    const qty = typeof s.qty === "number" ? s.qty : 0;
    const avgEntryPrice =
      typeof s.avgFillPrice === "number" ? s.avgFillPrice : 0;
    const currentPrice =
      typeof s.currentUnitPrice === "number" ? s.currentUnitPrice : 0;
    const marketValue = qty * currentPrice;
    const costBasis = qty * avgEntryPrice;
    const totalPLDollar = marketValue - costBasis;
    // Store as a fraction (-0.0364), not a percentage (-3.64). The
    // rest of the app — formatPctSigned, the simple-view delta line,
    // the drawer's percent display — multiplies by 100 at render
    // time, so feeding in a percentage here would 100× the rendered
    // value (Sergey 2026-04-26 caught V/BDX showing -364%/-612%).
    const totalPLPct =
      typeof s.valueChange === "number"
        ? s.valueChange / 100
        : costBasis > 0
          ? totalPLDollar / costBasis
          : 0;
    out.push({
      id: `${accountId}:${s.symbol}`,
      accountId,
      asset: s.symbol,
      // Prefer the broker's own company name when present; otherwise
      // fall back to our static ticker → long-name table so the
      // detail drawer can render "KO · The Coca-Cola Company" instead
      // of the misleading "KO · KO" duplicate.
      assetName:
        typeof s.company === "string" && s.company.length > 0
          ? s.company
          : resolveAssetName(s.symbol),
      assetClass: "stocks",
      side: "long",
      qty: Math.abs(qty),
      avgEntryPrice,
      currentPrice,
      marketValue,
      costBasis,
      dayChangePct: 0,
      todayPLDollar: 0,
      todayPLPct: 0,
      totalPLDollar,
      totalPLPct,
      sparkline: [],
      openedAt: earliestBuyByTicker.get(s.symbol) ?? "",
    });
  }
  return out;
}

/**
 * Row shape of `POST /trading-history` (per the recon doc §3).
 * Pagination lives outside this interface (it's a top-level wrapper).
 * Newest-first on `date`, so callers that want ascending need to
 * reverse.
 */
export interface RawTradingHistoryRow {
  id: string;
  brokerAccountId?: string;
  botId?: string | null;
  date?: string;
  symbol?: string;
  icon?: string;
  /** `1` Buy · `2` Sell (verified against UI copy). */
  action?: number;
  /** `4` observed as "Filled"; other values not yet mapped. */
  state?: number;
  /** Enum, values not yet mapped beyond the observed `4`. */
  strategy?: number;
  qty?: number;
  price?: number;
  total?: number;
}

export interface RawTradingHistoryResponse {
  totalItems?: number;
  page?: number;
  pageSize?: number;
  records?: RawTradingHistoryRow[];
}

/** Order `action` enum → PWA side. 1 = Buy, 2 = Sell (verified). */
function actionToSide(action: number | undefined): OrderSide {
  return action === 2 ? "sell" : "buy";
}

/** Order `state` enum → PWA status. Only `4` ("Filled") has been seen on
 *  real data, so other values currently collapse to "filled" with an
 *  `__unknown__` stateDetail. Refine when more examples show up. */
function stateToStatus(state: number | undefined): {
  status: OrderStatus;
  stateDetail?: string;
} {
  if (state === 4) return { status: "filled" };
  return {
    status: "filled",
    stateDetail: state === undefined ? undefined : `state=${state}`,
  };
}

/**
 * Convert a single raw trading-history row into the PWA's `Order`
 * shape. Fields we can't pull from the API (orderType, notional,
 * limit/stop prices, scheduling metadata) are left undefined or set
 * to safe defaults (orderType → "market") — the Simple/Advanced
 * views tolerate the missing fields.
 *
 * Row shape documented in `docs/WHB-API-RECON.md` §3.
 */
export function mapRealTradeToOrder(row: RawTradingHistoryRow): Order | null {
  if (!row || typeof row.id !== "string" || !row.id) return null;
  if (typeof row.symbol !== "string" || !row.symbol) return null;
  const side = actionToSide(row.action);
  const filledQty = typeof row.qty === "number" ? row.qty : 0;
  const avgFillPrice = typeof row.price === "number" ? row.price : 0;
  const totalAmount =
    typeof row.total === "number" ? row.total : filledQty * avgFillPrice;
  const { status, stateDetail } = stateToStatus(row.state);
  const submittedAt = typeof row.date === "string" ? row.date : "";
  // Filled rows carry the fill time in `date`; there's no separate
  // placement timestamp in the API, so mirror into filledAt so
  // downstream views that prefer filledAt stay populated.
  const filledAt = status === "filled" ? submittedAt : undefined;
  const botId =
    typeof row.botId === "string" && row.botId.length > 0 ? row.botId : undefined;

  // `orderType` isn't on the API. Default to market — the one pattern
  // we've seen from WHB's bots. Refine when the backend starts
  // surfacing type info (e.g. new field) or when we want smarter
  // inference (e.g. non-zero limit price → "limit").
  const orderType: OrderType = "market";

  return {
    id: row.id,
    accountId: typeof row.brokerAccountId === "string" ? row.brokerAccountId : "",
    botId,
    asset: row.symbol,
    // /trading-history rows don't carry a company name. Resolve via
    // the static ticker → long-name table so the order detail drawer
    // shows "IT · Gartner, Inc." instead of "IT · IT". When the
    // ticker isn't in the table the helper returns the symbol
    // unchanged, and the UI suppresses the duplicate downstream.
    assetName: resolveAssetName(row.symbol),
    side,
    orderType,
    filledQty,
    avgFillPrice,
    totalAmount,
    status,
    source: botId ? "bot" : "manual",
    submittedAt,
    filledAt,
    ...(stateDetail ? { stateDetail } : {}),
    // Strategy enum is not yet decoded beyond the observed `4`. Pass
    // the raw number as a string so Advanced view can surface it while
    // we finish reverse-engineering the full mapping.
    ...(typeof row.strategy === "number"
      ? { strategy: `s${row.strategy}` }
      : {}),
  };
}

/**
 * Row shape of `GET /broker-accounts/{id}/orders?from=…&to=…` —
 * the "Link Orders" endpoint that backs the Broker Orders page in
 * WHB's web UI. Probed live 2026-04-30 against the Default Live
 * account (Apr 29-30 returned 12 rows, all manual sells from when
 * the user closed positions directly in Alpaca after deleting their
 * bot).
 *
 * Important fields (vs `/trading-history`):
 *   • `externalOrderId`         — Alpaca's order id, the dedup key
 *                                  against the trading-history feed
 *                                  (which carries it in its own `id`
 *                                  field once an order is linked).
 *   • `tradingHistoryRecordId`  — `null` for orders that haven't been
 *                                  attributed to any bot ("missed").
 *                                  Non-null once the user links it
 *                                  via the UI's "Assign Bot" button.
 *   • `portfolioContextId`      — bot id once linked; `null` while
 *                                  unlinked.
 *   • `botName`                 — display name once linked; `null`
 *                                  while unlinked.
 *   • `action`                  — same enum as trading-history
 *                                  (1 = Buy, 2 = Sell).
 *   • `date`                    — ISO timestamp (not just a date).
 *
 * The orders this surface returns are USER-INITIATED MANUAL fills in
 * Alpaca that didn't go through any WHB bot. Without consuming them,
 * equity reconstruction sees the resulting position changes as
 * unexplained gains/losses.
 */
export interface RawMissedOrderRow {
  externalOrderId?: string;
  symbol?: string;
  /** 1 = Buy, 2 = Sell. Matches `/trading-history.action`. */
  action?: number;
  requestedQty?: number;
  requestedTotalPrice?: number | null;
  filledQty?: number;
  avgFillPrice?: number;
  /** ISO timestamp — `2026-04-29T19:20:15.318087Z`. */
  date?: string;
  isFilled?: boolean;
  /** `null` while the order hasn't been attributed to any bot's
   *  trading history; non-null once the user clicks "Assign Bot". */
  tradingHistoryRecordId?: string | null;
  /** Bot id while linked; `null` while unlinked. */
  portfolioContextId?: string | null;
  botName?: string | null;
}

/**
 * Map a raw "Link Orders" row into the PWA's `Order` shape.
 *
 * Filters applied at this layer:
 *   • Skip rows with no `externalOrderId` or `symbol` (defensive).
 *   • Skip rows where `isFilled !== true` — only consume settled
 *     fills; in-flight orders shouldn't move the equity curve.
 *
 * `botId` is left undefined for unlinked rows so the order's
 * `source` lands as `"manual"` (see `mapRealTradeToOrder`). When the
 * order DOES carry a `portfolioContextId`, use it — but in practice
 * if the bot is still active and linked, the order will already
 * appear in `/trading-history` and the dedup logic upstream will
 * keep only one copy.
 */
export function mapRealMissedOrderToOrder(
  row: RawMissedOrderRow,
  accountId: string
): Order | null {
  if (!row || typeof row.externalOrderId !== "string" || !row.externalOrderId) {
    return null;
  }
  if (typeof row.symbol !== "string" || !row.symbol) return null;
  if (row.isFilled !== true) return null;

  const side = actionToSide(row.action);
  const filledQty = typeof row.filledQty === "number" ? row.filledQty : 0;
  const avgFillPrice =
    typeof row.avgFillPrice === "number" ? row.avgFillPrice : 0;
  const totalAmount = filledQty * avgFillPrice;
  const submittedAt = typeof row.date === "string" ? row.date : "";
  const botId =
    typeof row.portfolioContextId === "string" &&
    row.portfolioContextId.length > 0
      ? row.portfolioContextId
      : undefined;

  // Sergey 2026-04-30 — dedup key picked carefully:
  //
  // /broker-accounts/{id}/orders rows fall into TWO buckets:
  //   • LINKED  — `tradingHistoryRecordId` non-null. The same Alpaca
  //               fill is ALSO in /trading-history under that record
  //               id; including both rows would double-count the trade
  //               in the equity reconstruction (buys inflate stocks,
  //               sells double-credit cash).
  //   • ORPHAN  — `tradingHistoryRecordId` null. Only on this surface
  //               (e.g. manual sells the user made in Alpaca after
  //               deleting a bot). These need to flow into the recon
  //               so the cash side balances against today's withdrawal.
  //
  // The merge step in `useRealAccountOrders` dedups by `Order.id`.
  // If we use the LINKED row's `tradingHistoryRecordId` as Order.id,
  // dedup matches against the /trading-history Order.id and the
  // duplicate is dropped. ORPHAN rows fall back to the Alpaca
  // `externalOrderId` — unique, nothing to dedup against, they stay.
  const orderId = row.tradingHistoryRecordId || row.externalOrderId;
  return {
    id: orderId,
    accountId,
    botId,
    asset: row.symbol,
    assetName: resolveAssetName(row.symbol),
    side,
    orderType: "market",
    filledQty,
    avgFillPrice,
    totalAmount,
    status: "filled",
    source: botId ? "bot" : "manual",
    submittedAt,
    filledAt: submittedAt,
    // Mark synthetic so the SGOV-gap reconciler can tell apart fills
    // that originated on this surface (orphan sells from a deleted
    // bot) from regular /trading-history fills. The recon engine
    // doesn't filter by this — it processes all orders identically;
    // the flag is informational for downstream surfaces.
    synthetic: true,
  };
}

/**
 * Map an entire `/broker-accounts/{id}/orders` payload (an array)
 * into the PWA's `Order[]`. Drops malformed rows silently.
 *
 * Sergey 2026-04-30: per-account integration uses this in addition
 * to `/trading-history` so manual Alpaca fills (after a bot
 * deletion, mostly) show up in the equity reconstruction. Dedup
 * against the trading-history feed happens at the merge site —
 * this mapper just normalises the shape.
 */
export function mapRealMissedOrders(
  raw: unknown,
  accountId: string
): Order[] {
  if (!Array.isArray(raw)) return [];
  const out: Order[] = [];
  for (const r of raw as RawMissedOrderRow[]) {
    const order = mapRealMissedOrderToOrder(r, accountId);
    if (order) out.push(order);
  }
  return out;
}

/**
 * Map the whole `/trading-history` response payload. Filters out any
 * rows that fail basic sanity (no id, no symbol). Returns an empty
 * array on unrecognized payloads.
 */
export function mapRealTradingHistory(raw: unknown): Order[] {
  const records =
    raw && typeof raw === "object" && Array.isArray((raw as RawTradingHistoryResponse).records)
      ? (raw as RawTradingHistoryResponse).records!
      : Array.isArray(raw)
        ? (raw as RawTradingHistoryRow[])
        : [];
  const out: Order[] = [];
  for (const r of records) {
    const order = mapRealTradeToOrder(r);
    if (order) out.push(order);
  }
  return out;
}

/**
 * Row shape of `GET /bots` — the richer-than-embedded bot list across
 * every account the user has (not per-account). Documented in
 * `docs/WHB-API-RECON.md` §4.
 *
 * The list endpoint does NOT include the parent account id directly;
 * only a human-readable `brokerAccountName`. The PWA attributes a bot
 * to an account by consulting the account-detail response's `bots[]`
 * array, which carries the bot `id` — `account.botIds` already
 * holds this mapping (see `mapRealBrokerAccountDetail`). So to get
 * "bots on account X", filter the `/bots` list by
 * `account.botIds.includes(bot.id)`.
 */
export interface RawBotListRow {
  id?: string;
  name?: string;
  displayName?: string;
  brokerAccountName?: string;
  group?: number;
  type?: number;
  /** `2` Active observed; other values not yet mapped. */
  status?: number;
  statusDate?: string;
  allocatedAmount?: number;
  /** Current $ value of the bot's slice of the portfolio. */
  portfolioValue?: number;
  /** Historical value change — sign + units not yet verified. Treated
   *  as a $ delta for now since that matches the magnitude observed
   *  alongside `portfolioValue`. */
  portfolioValueChange?: number;
  lastTradeDate?: string;
  history?: unknown;
  icon?: string;
}

/**
 * Convert a raw `/bots` row into the PWA's `Bot` shape.
 *
 * Field coverage notes:
 *   • `status` → `"ACTIVE"` if numeric 2 else `"PAUSED"`. We only have
 *     one verified status value so this is best-effort; refine once
 *     more samples land.
 *   • `currentValue` ← `portfolioValue`.
 *   • `allTimeGain*` derived from `portfolioValue − allocatedAmount`.
 *     Backend may offer its own computed field later.
 *   • `netDeposits` treated as `allocatedAmount` (the user-controlled
 *     slice of account cash handed to the bot).
 *   • `tickers`, `description`, `buyingPower`, `twrPct`, `apyPct`,
 *     `sharpe`, `maxDDPct`, `dayChangePct`, `sparkline` — not on this
 *     endpoint; default to zero / empty. When a per-bot detail
 *     endpoint lands, a separate overlay hook can fill these in.
 *
 * The required `accountId` field is resolved by the caller — the
 * `/bots` list doesn't carry it — so this function takes it as a
 * parameter. Pass empty string if you're converting before you know
 * the owning account.
 */
export function mapRealBotToPwa(raw: RawBotListRow, accountId: string): Bot | null {
  if (!raw || typeof raw.id !== "string" || !raw.id) return null;
  const allocatedAmount =
    typeof raw.allocatedAmount === "number" ? raw.allocatedAmount : 0;
  const currentValue =
    typeof raw.portfolioValue === "number" ? raw.portfolioValue : 0;
  const allTimeGain = currentValue - allocatedAmount;
  // `allTimeGainPct` is stored as a DECIMAL (0.113 → +11.3%) to match
  // the convention mock-data uses and that BotsSimpleView's render
  // expects (multiplies by 100 at display time). Sergey 2026-04-26:
  // previously this was `* 100` here AND in the view, producing
  // -10000.00% for any bot whose allocated capital was zeroed out.
  const allTimeGainPct =
    allocatedAmount > 0 ? allTimeGain / allocatedAmount : 0;
  const name =
    typeof raw.name === "string" && raw.name.length > 0
      ? raw.name
      : typeof raw.displayName === "string"
        ? raw.displayName
        : raw.id;
  return {
    id: raw.id,
    name,
    accountId,
    status: raw.status === 2 ? "ACTIVE" : "PAUSED",
    allocatedAmount,
    currentValue,
    // Percent perf stored as a decimal (matches existing Bot shape —
    // mock dataset uses e.g. 0.113 for +11.3%).
    performancePct: allocatedAmount > 0 ? allTimeGain / allocatedAmount : 0,
    lastTradeDate: typeof raw.lastTradeDate === "string" ? raw.lastTradeDate : "",
    sparkline: [],
    tickers: [],
    description:
      typeof raw.displayName === "string" && raw.displayName !== name
        ? raw.displayName
        : "",
    buyingPower: 0,
    allTimeGain,
    allTimeGainPct,
    // statusDate from /bots is a full ISO timestamp ("2025-06-13T17:29:51Z");
    // mock-data convention is yyyy-mm-dd. Slice to the date portion so
    // every downstream helper that does `new Date(\`${createdAt}T00:00:00Z\`)`
    // (e.g. `deriveBotReportMetrics` for ageDays) parses cleanly. Without
    // this, age renders as `NaN years` (Sergey 2026-04-26).
    createdAt:
      typeof raw.statusDate === "string" && raw.statusDate.length >= 10
        ? raw.statusDate.slice(0, 10)
        : "",
    // Report-style metrics — not surfaced by the bots list. Leave at
    // zero so the Advanced table renders 0.00 rather than NaN; a
    // future per-bot detail call can backfill.
    twrPct: 0,
    apyPct: 0,
    sharpe: 0,
    maxDDPct: 0,
    netDeposits: allocatedAmount,
    dayChangePct: 0,
  };
}

/**
 * Map the whole `/bots` list payload. Skips malformed rows. Does NOT
 * populate `accountId` — that's the caller's responsibility (match
 * by id against `account.botIds`).
 */
export function mapRealBotList(raw: unknown): Bot[] {
  const arr = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown[] }).items)
      ? ((raw as { items: unknown[] }).items)
      : [];
  const out: Bot[] = [];
  for (const r of arr) {
    const bot = mapRealBotToPwa(r as RawBotListRow, "");
    if (bot) out.push(bot);
  }
  return out;
}

/**
 * Unwrap the API response for activities into a typed array. The real
 * endpoint returns a flat array; defensive against a wrapped
 * `{ records: [...] }` shape (not observed but cheap to tolerate).
 */
export function unwrapActivities(raw: unknown): RawAccountActivity[] {
  const arr = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { records?: unknown[] }).records)
      ? (raw as { records: unknown[] }).records
      : [];
  return arr.filter(
    (r): r is RawAccountActivity =>
      !!r &&
      typeof r === "object" &&
      typeof (r as RawAccountActivity).id === "string" &&
      typeof (r as RawAccountActivity).timestamp === "string" &&
      typeof (r as RawAccountActivity).name === "string" &&
      typeof (r as RawAccountActivity).amount === "number"
  );
}

const EMPTY_RISK: RiskMetrics = {
  wprPct: 0,
  wprSpyPct: 0,
  wprSpyTotalPct: 0,
  apyPct: 0,
  apySpyPct: 0,
  apySpyTotalPct: 0,
  volPct: 0,
  volSpyPct: 0,
  volSpyTotalPct: 0,
  maxDDPct: 0,
  maxDDSpyPct: 0,
  maxDDSpyTotalPct: 0,
  beta: 0,
  alphaPct: 0,
  sharpe: 0,
  sharpeSpy: 0,
  sharpeSpyTotal: 0,
  calmar: 0,
  calmarSpy: 0,
  calmarSpyTotal: 0,
};

/** Pull "Alpaca" (or similar) out of a display name like
 *  `"Default Live (Alpaca Live)"`. Falls back to the bare `name` if no
 *  parens are present. */
function extractProvider(displayName?: string, fallbackName?: string): string {
  if (!displayName) return fallbackName ?? "Unknown";
  const m = displayName.match(/\(([^)]+)\)/);
  if (!m) return fallbackName ?? displayName;
  // "Alpaca Live" / "Alpaca Paper" / "WhiteHatBots (Paper)" — strip
  // trailing " Live"/" Paper" so the caller gets just the vendor.
  return m[1].replace(/\s+(Live|Paper)$/i, "").trim();
}

/**
 * Classify an account as paper/non-live.
 *
 * Authoritative source is `accountType`:
 *   - `4` (Live) → returns false
 *   - everything else (Paper=3, Sim=0, or anything we haven't seen) → true
 *
 * If `accountType` is missing for some reason, fall back to the old
 * substring heuristic so we don't misclassify a Live account as Paper
 * just because the enum field was absent.
 */
function inferPaper(raw: RawBrokerAccount): boolean {
  if (typeof raw.accountType === "number") {
    return raw.accountType !== ACCOUNT_TYPE.Live;
  }
  const hay = `${raw.name ?? ""} ${raw.displayName ?? ""}`.toLowerCase();
  // No accountType — be cautious and treat only explicit "Live" names as live.
  if (/\blive\b/.test(hay)) return false;
  return true;
}

/** Short identifier the selector renders after the name. We don't have
 *  a masked account number from /broker-accounts, so fall back to the
 *  last four hex chars of the externalId (the WHB API's stable per-
 *  account handle). Empty string is valid — the selector will just
 *  omit the `*XXXX` suffix. */
function deriveMasked(raw: RawBrokerAccount): string {
  const id = raw.externalId ?? raw.id;
  if (!id) return "";
  const tail = id.replace(/-/g, "").slice(-4);
  return tail ? `*${tail}` : "";
}

/**
 * Convert a raw /broker-accounts row into the shape the rest of the
 * app consumes. All value-bearing fields are zeroed; `source: "real"`
 * lets the UI render them as pending.
 */
export function mapRealBrokerAccount(raw: RawBrokerAccount): BrokerAccount {
  return {
    id: raw.id,
    source: "real",
    botsCount: typeof raw.botsCount === "number" ? raw.botsCount : 0,
    name: raw.name ?? "Unnamed account",
    provider: extractProvider(raw.displayName, raw.name),
    masked: deriveMasked(raw),
    paper: inferPaper(raw),

    // Balances / holdings — unknown until the balances endpoint is wired.
    portfolioValue: 0,
    buyingPower: 0,
    buyingPowerExBots: 0,
    stocksValue: 0,
    cashValue: 0,
    stocksCount: 0,
    stocksDayChangePct: 0,
    allTimeChange: 0,
    allTimeChangePct: 0,

    // Curves / benchmarks — unknown. Empty arrays keep the chart
    // component from throwing but the hero will skip rendering it
    // for real accounts (see AccountsPageV2 guard).
    spyReturnPct: 0,
    twrCurve: [],
    spyCurve: [],
    spyTotalCurve: [],
    curveDates: [],

    risk: { ...EMPTY_RISK },
    targetApyPct: 20,
    cashflows: [],
    equityCurve: [],
    botIds: [],
  };
}

export function mapRealBrokerAccounts(raw: unknown): BrokerAccount[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is RawBrokerAccount => !!r && typeof r === "object" && typeof (r as RawBrokerAccount).id === "string")
    .map(mapRealBrokerAccount);
}

/**
 * Convert a raw GET `/broker-accounts/{id}` detail row into a
 * partial `BrokerAccount` overlay containing ONLY the fields the
 * detail endpoint can populate — hero numbers, equity curve, open
 * positions count, attached bot ids.
 *
 * This deliberately returns `Partial<BrokerAccount>` and omits
 * identity fields (`name`, `provider`, `masked`, `paper`) because
 * the `/broker-accounts/{id}` response does NOT include the parent
 * `displayName` string that `mapRealBrokerAccount` needs to extract
 * the broker label ("Alpaca" / "WhiteHatBots"). The list endpoint
 * is the authoritative source for those fields; callers should
 * spread this overlay on top of the list-row shape:
 *
 *   const account = { ...listRow, ...detailOverlay };
 *
 * That keeps "Alpaca" showing in the hero subtitle even though the
 * detail response has only a bare `name`.
 *
 * Fields we **don't** derive here (still left as empty/zero on the
 * list row, since detail doesn't surface them either):
 *
 *   • `twrCurve`, `spyCurve`, `spyTotalCurve`, `spyReturnPct` —
 *     need SPY price/total-return series + TWR chain-linking. The
 *     PortfolioVsSpyChart consumes them only for the benchmark
 *     overlay; the portfolio line itself derives from `equityCurve`
 *     (plus `cashflows`) so the chart still renders meaningfully
 *     with these empty.
 *   • `risk.*` (Sharpe, MaxDD, APY, …) — need the same TWR series
 *     above. Left as zero; MetricsPanel renders 0.00 until a real
 *     computation lands.
 *   • `cashflows[]` — no deposit/withdrawal history in this endpoint.
 *     Without it the "Net Deposits" chip on the chart will show zero
 *     for real accounts.
 *   • `stocksDayChangePct` — no day-over-day snapshot on the
 *     `portfolio` object.
 *   • `buyingPowerExBots` — the API returns one `buyingPower` figure
 *     with no "ex-bots" split. Mirror it as a safe placeholder.
 */
export function mapRealBrokerAccountDetail(
  raw: RawBrokerAccountDetail,
  activities?: readonly RawAccountActivity[] | null,
  spyPrices?: readonly SpyPricePoint[] | null,
  orders?: readonly Order[] | null,
  historicalQuotes?: HistoricalQuotesMap | null,
  historicalSplits?: HistoricalSplitsMap | null
): Partial<BrokerAccount> {
  const portfolio = raw.portfolio ?? {};
  const allStates = Array.isArray(raw.states) ? raw.states : [];
  // Account-level rows only (botId === null). Per the recon doc these
  // are sorted newest-first by the server — flip to ascending so the
  // chart renders oldest → newest on the x-axis.
  const accountStates = allStates
    .filter(
      (s): s is RawAccountState =>
        !!s &&
        (s.botId === null || s.botId === undefined) &&
        typeof s.timestamp === "string"
    )
    .slice()
    .sort((a, b) =>
      String(a.timestamp).localeCompare(String(b.timestamp))
    );

  // Equity basis for margin accounts: cash + stocks. `margin` on the
  // state row is *available credit* (the broker's extension of buying
  // power), NOT a component of account equity — adding it inflates
  // the curve by the full margin line (e.g. Default Live: 107060.88
  // cash + (-16199.50) stocks + ~250k margin → $341k, vs the correct
  // ~$91k portfolio.equity shown on the WHB landing page). If a
  // sub-component is missing on a row treat it as 0 so one bad sample
  // doesn't NaN the whole series.
  const stateEquity = accountStates.map(
    (s) => (s.cash ?? 0) + (s.stocks ?? 0)
  );
  const stateDates = accountStates.map((s) =>
    String(s.timestamp).slice(0, 10)
  );

  // Convert real activities (if supplied) into the PWA's Cashflow[]
  // shape. Only user cashflows land here — dividends, interest, and
  // fees are returns, not deposits, and would corrupt the ND line +
  // metrics card if included. For the full-history reconstruction
  // further down we use a SEPARATE cashflow list that includes every
  // income event (dividends/interest/fees) too, because those DO move
  // cash — just not deposits.
  const cashflows =
    activities && activities.length > 0 ? activitiesToCashflows(activities) : null;

  // Full-history reconstruction from primary-source data (orders +
  // activities + historical quotes) when all three are available.
  // Per Sergey 2026-04-22: "calculate daily balance […] from the
  // activity and orders, real positions on each day" — the state
  // snapshots have errors both pre- and post-Sep-14, so we bypass
  // them entirely when we have the means to rebuild from scratch.
  //
  // The reconstruction walks day by day, applying every cash event
  // and every fill, then marks each surviving position to market at
  // that day's close. Result is a daily (cash, stocksValue, equity)
  // trace from first-activity through today — exactly what the chart
  // needs, and replaces BOTH the `states[]`-derived middle section
  // AND the pre-state cumulative-deposit backfill.
  //
  // Fallback: when we don't yet have orders or prices (hook hasn't
  // landed, price fetch failed), fall back to the prior path —
  // pre-state backfill prepended to states-derived equity. Acceptable
  // degradation so the UI still renders while the richer source is
  // loading.
  let equityCurve: number[];
  let curveDates: string[];

  // SGOV-as-cash workaround: compute the current market value of any
  // cash-equivalent positions (SGOV etc.) from raw.stocks[]. Used
  // below to reclassify cash → stocks on every reconstruction day so
  // cash matches Alpaca's books even though WHB filters SGOV out of
  // `/trading-history`. See `CASH_EQUIVALENT_SYMBOLS` above for the
  // full rationale.
  const rawStocksArr = Array.isArray(raw.stocks) ? raw.stocks : [];
  const cashEquivalentMV = rawStocksArr.reduce((sum, s) => {
    if (!s || !isCashEquivalent(s.symbol)) return sum;
    const qty = typeof s.qty === "number" ? s.qty : 0;
    const px = typeof s.currentUnitPrice === "number" ? s.currentUnitPrice : 0;
    return sum + qty * px;
  }, 0);

  const reconCashflowsForCash =
    activities && activities.length > 0
      ? activities.map<Cashflow>((a) => ({
          date: String(a.timestamp).slice(0, 10),
          amount: a.amount,
          type: a.amount >= 0 ? "deposit" : "withdrawal",
        }))
      : null;
  // Split the incoming orders feed into (a) real broker orders from
  // /trading-history and (b) synthetic corrections the UI appended
  // from IndexedDB. The reconstruction itself RUNS TWICE below:
  //   1) "bare" on real orders only — to measure the gap vs
  //      portfolio.cash (authoritative Alpaca cash) and decide the
  //      correction amount.
  //   2) "full" including corrections — the numbers the chart + hero
  //      actually consume, so every day's cash matches Alpaca (once
  //      the correction is in play).
  // This mirrors how a real accountant would book a reconciliation
  // entry: compute what's missing, post it, then re-derive.
  // Reconstruction needs broker-history orders + Link-Orders missed
  // fills. Drop:
  //   • `orderType === "correction"` — IndexedDB-persisted SGOV-gap
  //                                    corrections; already accounted
  //                                    for via the bare→full step.
  //
  // Missed orders (from /broker-accounts/{id}/orders, marked
  // `synthetic: true`) DO flow in. The recon's sell handler clamps
  // the position deduction at the held qty, so an orphan sell
  // (matching buy not in /trading-history) credits cash without
  // tracking a phantom short. This restores the cash trajectory
  // when a bot has been deleted but its post-deletion liquidations
  // still need to settle into the equity curve. Sergey 2026-04-30:
  // earlier filter `!o.synthetic` was too aggressive — once the
  // recon engine clamps sells correctly, missed orders are safe to
  // include and necessary for the cash side to balance.
  const realOrders =
    orders?.filter((o) => o && o.orderType !== "correction") ?? null;
  const existingCorrections =
    orders?.filter((o) => o && o.orderType === "correction") ?? [];

  const baseReconInput =
    realOrders &&
    reconCashflowsForCash &&
    historicalQuotes &&
    Object.keys(historicalQuotes).length > 0
      ? {
          cashflows: reconCashflowsForCash,
          orders: realOrders,
          prices: historicalQuotes,
          splits: historicalSplits ?? undefined,
        }
      : null;

  const bareReconstructed = baseReconInput
    ? reconstructEquityCurveWithStats(baseReconInput)
    : null;

  // Compute the cash gap between the bare reconstruction and Alpaca's
  // authoritative cash (portfolio.cash is pulled straight from Alpaca
  // by WHB's backend, so it reflects SGOV trades + every other event
  // we can't see). Persist that gap as an SGOV-gap correction — it
  // will live in IndexedDB and survive sign-out, so the next page
  // load starts with it already applied.
  const bareFinalCash = bareReconstructed?.days.length
    ? bareReconstructed.days[bareReconstructed.days.length - 1]!.cash
    : null;
  const portfolioCashForCorrection =
    typeof portfolio.cash === "number" ? portfolio.cash : null;
  const correctionDiff =
    bareFinalCash !== null && portfolioCashForCorrection !== null
      ? portfolioCashForCorrection - bareFinalCash
      : 0;

  // Best-effort fire-and-forget upsert. Idempotent: same diff on the
  // next render writes the same row; a near-zero diff removes the
  // correction so the Orders tab auto-cleans once WHB fixes SGOV.
  //
  // Correction date picks the EARLIEST activity date we know about
  // (first CashDeposit or first order, whichever is earlier). That is
  // when SGOV purchases most plausibly started — dating the correction
  // there means the chart's cash curve shows the drop at account
  // inception rather than as a cliff on "today", which better
  // reflects how Alpaca actually sees the account. Accurate
  // per-trade dating would require parsing the user's Alpaca export
  // (not a WHB API call we have access to); TODO for a future pass.
  //
  // Progressive-load safety: during the cache-first paint the orders
  // list may be the IndexedDB-cached subset rather than the full
  // network payload — reconstruction with partial orders produces a
  // bogus (often huge) bareFinalCash. Guard against persisting garbage
  // by: (a) skipping the upsert if |diff| exceeds a sanity threshold
  // ($5,000 — real SGOV gaps observed are ≤ $200), and (b) requiring
  // historicalQuotes to have at least one quote per unique ticker in
  // the order history (a rough proxy for "reconstruction inputs are
  // complete"). Both gates let the correct render win on a later
  // React pass without writing a corrupted value in between.
  const MAX_PLAUSIBLE_CORRECTION = 5000;
  const ordersComplete =
    realOrders &&
    historicalQuotes &&
    (() => {
      const tickers = new Set<string>();
      for (const o of realOrders) {
        if (o && typeof o.asset === "string") tickers.add(o.asset.toUpperCase());
      }
      // Require at least 90% of held tickers to have price data before
      // we trust the reconstruction's final cash. The cache-first
      // paint typically has zero quotes until the network resolves.
      if (tickers.size === 0) return false;
      const priced = Array.from(tickers).filter(
        (t) => Array.isArray(historicalQuotes[t]) && historicalQuotes[t]!.length > 0
      ).length;
      return priced / tickers.size >= 0.9;
    })();
  if (
    bareFinalCash !== null &&
    portfolioCashForCorrection !== null &&
    ordersComplete &&
    Math.abs(correctionDiff) <= MAX_PLAUSIBLE_CORRECTION
  ) {
    // Earliest activity timestamp → ISO date.
    let earliestDate = new Date().toISOString().slice(0, 10);
    if (activities && activities.length > 0) {
      let minTs: string | undefined;
      for (const a of activities) {
        const ts = typeof a?.timestamp === "string" ? a.timestamp : "";
        if (!minTs || ts < minTs) minTs = ts;
      }
      if (minTs) earliestDate = minTs.slice(0, 10);
    }
    // Earliest order date — take the min if it predates activities.
    if (orders && orders.length > 0) {
      let minOrderDate: string | undefined;
      for (const o of orders) {
        if (o?.orderType === "correction") continue;
        const d = typeof o?.submittedAt === "string" ? o.submittedAt.slice(0, 10) : "";
        if (d && (!minOrderDate || d < minOrderDate)) minOrderDate = d;
      }
      if (minOrderDate && minOrderDate < earliestDate) earliestDate = minOrderDate;
    }
    if (Math.abs(correctionDiff) > 0.005 && typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.log(
        `[whb-reconstruct ${raw.id.slice(0, 8)}] correction: diff $${correctionDiff.toFixed(2)} (bare $${bareFinalCash.toFixed(2)} → portfolio.cash $${portfolioCashForCorrection.toFixed(2)}) — persisting as SGOV correction Order dated ${earliestDate}`
      );
    }
    void upsertSgovGapCorrection({
      accountId: raw.id,
      diff: correctionDiff,
      asOfIso: earliestDate,
    });
  } else if (
    bareFinalCash !== null &&
    portfolioCashForCorrection !== null &&
    Math.abs(correctionDiff) > MAX_PLAUSIBLE_CORRECTION &&
    typeof window !== "undefined"
  ) {
    // Large diff + orders-incomplete = mid-load render; skipping the
    // upsert avoids persisting a garbage value. The log helps spot a
    // legitimate large gap if one ever occurs in practice.
    // eslint-disable-next-line no-console
    console.warn(
      `[whb-reconstruct ${raw.id.slice(0, 8)}] correction skipped: diff $${correctionDiff.toFixed(2)} exceeds plausibility ceiling $${MAX_PLAUSIBLE_CORRECTION} or orders-not-complete yet — will retry on next render`
    );
  }

  // Final reconstruction = bare reconstruction + correction applied
  // as a closing-day sell/buy. We don't re-run the engine — we just
  // shift the final day (and all historical days by the same amount)
  // so the chart end-point matches Alpaca. The shift is equivalent
  // to inserting a single dated trade on `today` with totalAmount =
  // |diff|, which is exactly what the persisted correction Order
  // represents. Historical days carry the same constant offset,
  // which is acceptable (their shape is preserved).
  //
  // Sergey 2026-05-01 (revert): always apply the SGOV-gap shift —
  // WHB's states[] don't match Alpaca's portfolio history convention
  // (off by $2-3k for margin accounts), so the previous experiment
  // of preferring states post-Sep-15 produced curves that didn't
  // match Alpaca. The pure-reconstruction path WITH the shift
  // anchors today's value to Alpaca's portfolio.cash and the rest of
  // the curve carries the same offset, which matched Alpaca exactly
  // before the experiment. Restored.
  const reconstructedWithStats = bareReconstructed;
  const reconstructed = reconstructedWithStats?.days ?? null;
  if (reconstructed && reconstructed.length > 0 && Math.abs(correctionDiff) > 0.005) {
    for (let i = 0; i < reconstructed.length; i++) {
      const d = reconstructed[i]!;
      reconstructed[i] = {
        ...d,
        cash: +(d.cash + correctionDiff).toFixed(2),
        equity: +(d.equity + correctionDiff).toFixed(2),
      };
    }
  }
  // existingCorrections isn't fed into the reconstruction because the
  // shift above already incorporates the equivalent cash delta. We
  // keep the variable around so the Orders tab (which reads the same
  // `orders` array) still shows it — no action needed here.
  void existingCorrections;

  // Stocks anchor (final-day only). Our reconstruction marks every
  // day's stocks at the EOD close price we get from Massive (yesterday
  // at earliest). Alpaca's `portfolio.stocksValue` shows today's
  // current or close price — which the user sees in the hero and
  // expects our number to match. On non-trading days the two agree
  // (last close = current close). On active trading days they diverge
  // by up to the day's market move (observed ~3% = ~$1,500 on Ellen).
  //
  // Shift the LAST day's stocks so reconstructed equity matches
  // Alpaca's portfolio.equity to the cent. Historical days are left
  // on their EOD-mark-to-market basis — their shape reflects actual
  // historical price action, not today's snapshot. This creates a
  // tiny visual "step" between the second-to-last day and today
  // which correctly represents the day's real-time market move.
  if (reconstructed && reconstructed.length > 0) {
    const portfolioStocks =
      typeof portfolio.stocksValue === "number" ? portfolio.stocksValue : null;
    if (portfolioStocks !== null) {
      const lastIdx = reconstructed.length - 1;
      const lastDay = reconstructed[lastIdx]!;
      const stocksAnchorDelta = +(portfolioStocks - lastDay.stocksValue).toFixed(2);
      // How big can a LEGITIMATE last-day-only delta be? It represents
      // one day of market movement on the equity sleeve (EOD marks vs
      // Alpaca's live prices) — a few percent. Anything beyond that is
      // not a market move, it's a systematic reconstruction bias that
      // happens to be measurable on the final day.
      //
      // Sergey 2026-08-03: applying a systematic bias to the last day
      // ONLY is what produced the "$132k cliff" — Jul 31 sat on the
      // inflated reconstruction while Aug 3 was yanked down to
      // Alpaca's true $107k, a −$24,612 (−18.6%) vertical drop on the
      // final day that also poisoned every "previous day" metric.
      //
      // So: small delta → last day only (today's real market move,
      // original behaviour). Large delta → shift the WHOLE curve, the
      // same way the cash `correctionDiff` above already does, so the
      // series stays continuous AND still ends on Alpaca's number.
      const plausibleIntradayMove = Math.max(5000, Math.abs(portfolioStocks) * 0.05);
      const systematic = Math.abs(stocksAnchorDelta) > plausibleIntradayMove;
      if (Math.abs(stocksAnchorDelta) > 0.005) {
        if (typeof window !== "undefined") {
          // eslint-disable-next-line no-console
          console.log(
            `[whb-reconstruct ${raw.id.slice(0, 8)}] stocks anchor (${systematic ? "WHOLE CURVE — systematic bias" : "last day"}): shift $${stocksAnchorDelta.toFixed(2)} (reconstructed $${lastDay.stocksValue.toFixed(2)} → portfolio.stocksValue $${portfolioStocks.toFixed(2)})${systematic ? ` — exceeds plausible intraday move $${plausibleIntradayMove.toFixed(2)}; shifting all ${reconstructed.length} days to avoid a final-day cliff` : " — reflects today's live market prices vs Massive EOD"}`
          );
        }
        if (systematic) {
          for (let i = 0; i < reconstructed.length; i++) {
            const d = reconstructed[i]!;
            reconstructed[i] = {
              ...d,
              stocksValue: +(d.stocksValue + stocksAnchorDelta).toFixed(2),
              equity: +(d.equity + stocksAnchorDelta).toFixed(2),
            };
          }
        } else {
          reconstructed[lastIdx] = {
            ...lastDay,
            stocksValue: +(lastDay.stocksValue + stocksAnchorDelta).toFixed(2),
            equity: +(lastDay.equity + stocksAnchorDelta).toFixed(2),
          };
        }
      }
    }
  }

  // Emit a one-shot console summary so Sergey can `console` the
  // reconstruction inputs without opening devtools' React tree. This
  // is the only place in the PWA that calls `console.log` related to
  // reconstruction — easy to filter in DevTools.
  if (
    reconstructedWithStats &&
    typeof window !== "undefined" &&
    typeof window.console !== "undefined"
  ) {
    const s = reconstructedWithStats.stats;
    const miss = s.tickersMissingQuotes;
    // Activity-type subtotals — proves every activity flows into the
    // reconstruction's cash. Sum separately so a non-zero figure on
    // each row confirms that type contributed.
    const actTotals: Record<string, { count: number; sum: number }> = {};
    if (activities) {
      for (const a of activities) {
        const key = a.name || "Unknown";
        const bucket = (actTotals[key] ??= { count: 0, sum: 0 });
        bucket.count++;
        bucket.sum += typeof a.amount === "number" ? a.amount : 0;
      }
    }
    const actSummary = Object.entries(actTotals)
      .map(([name, { count, sum }]) => `${name}:${count}/${sum.toFixed(2)}`)
      .join(" ");
    // Source breakdown — which upstream provider served each of the
    // tickers we *did* resolve. Lets us see at a glance whether
    // Massive is carrying the load or we're leaning on the Yahoo
    // fallback. Lives in a module-global registry populated by the
    // quotes hooks as responses arrive.
    const sourceCounts: Record<string, number> = {};
    if (historicalQuotes) {
      for (const t of Object.keys(historicalQuotes)) {
        // Dynamic import-free access; the registry is on the same
        // barrel as the hooks. Using `globalThis` sidesteps the
        // circular-import cost of pulling `quoteSourceRegistry` into
        // the adapter just for this diagnostic.
        const src =
          (globalThis as { __whbQuoteSources?: Record<string, string> })
            .__whbQuoteSources?.[t] ?? "unknown";
        sourceCounts[src] = (sourceCounts[src] ?? 0) + 1;
      }
    }
    const srcSummary = Object.entries(sourceCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([src, n]) => `${src}:${n}`)
      .join(" ");
    // Last-N-days cash trail — so the user can compare to Alpaca's EOD
    // snapshot for any specific date. Cash must match PENNY-EXACT
    // against Alpaca on the same date.
    const recentDays = reconstructedWithStats.days.slice(-7);
    const dailyTrail = recentDays
      .map(
        (d) =>
          `${d.date}: cash=$${d.cash.toFixed(2)} stocks=$${d.stocksValue.toFixed(2)} eq=$${d.equity.toFixed(2)}`
      )
      .join(" | ");

    // Scan for orders whose `totalAmount` differs from `qty ×
    // avgFillPrice`. Alpaca fills can legitimately round (fractional
    // share x full-precision price), so differences of a few cents per
    // row are normal. But the CUMULATIVE delta across all orders is
    // what moves cash — if it matches the observed drift vs Alpaca,
    // the authoritative-cash-delta lives in `row.total` and the
    // adapter is already using it correctly (so the drift is
    // elsewhere — probably an activity we double-count or miss).
    // If the cumulative delta does NOT match the drift, that
    // effectively rules out the orders as the source and points us at
    // the activity feed.
    if (orders && orders.length > 0) {
      let totalDiff = 0;
      let rowsWithDiff = 0;
      let maxAbs = 0;
      let maxRow: { id: string; diff: number } | null = null;
      for (const o of orders) {
        if (!o) continue;
        const computed = o.filledQty * o.avgFillPrice;
        const diff = o.totalAmount - computed;
        if (Math.abs(diff) >= 0.005) {
          rowsWithDiff++;
          totalDiff += diff;
          if (Math.abs(diff) > maxAbs) {
            maxAbs = Math.abs(diff);
            maxRow = { id: o.id, diff };
          }
        }
      }
      if (rowsWithDiff > 0) {
        // eslint-disable-next-line no-console
        console.log(
          `[whb-reconstruct ${raw.id.slice(0, 8)}] order-total-vs-qty×price: ` +
            `${rowsWithDiff}/${orders.length} rows differ, ` +
            `Σdiff=$${totalDiff.toFixed(4)}, ` +
            `worst row=${maxRow?.id.slice(0, 8) ?? "—"} diff=$${maxRow?.diff.toFixed(4) ?? "—"}`
        );
      }
    }

    // Stash the full reconstruction run on `window` so the user can
    // inspect any single day's activities + orders from DevTools. This
    // is the tool for hunting penny-level cash discrepancies: once you
    // know WHICH day diverges from Alpaca, you can see the exact row
    // responsible. Cleared on reload. Guarded by a keyed registry so
    // multiple accounts don't clobber each other.
    //
    // Usage from DevTools:
    //   const run = __whbReconRuns["b87effeb"]           // account id prefix
    //   run.inputs.activities.filter(a =>
    //     a.timestamp.startsWith("2026-04-21"))
    //   run.inputs.orders.filter(o =>
    //     o.submittedAt?.startsWith("2026-04-21"))
    //   run.days.find(d => d.date === "2026-04-21")
    //   __whbInspectDay("b87effeb", "2026-04-21")        // helper below
    const g = globalThis as {
      __whbReconRuns?: Record<string, unknown>;
      __whbInspectDay?: (accId: string, date: string) => void;
    };
    g.__whbReconRuns ??= {};
    g.__whbReconRuns[raw.id] = {
      accountId: raw.id,
      inputs: {
        activities: activities ?? [],
        orders: orders ?? [],
      },
      days: reconstructedWithStats.days,
      stats: s,
    };
    // Prefix alias so `__whbReconRuns["b87effeb"]` works without the
    // full UUID. Overwrites on collision — prefix collisions are rare
    // in a per-user account set.
    const prefix = raw.id.slice(0, 8);
    if (prefix !== raw.id) {
      g.__whbReconRuns[prefix] = g.__whbReconRuns[raw.id];
    }
    // Install the day-inspector once — identical across accounts.
    if (!g.__whbInspectDay) {
      g.__whbInspectDay = (accId: string, date: string) => {
        const run = (g.__whbReconRuns as Record<string, {
          inputs: {
            activities: RawAccountActivity[];
            orders: Order[];
          };
          days: Array<{ date: string; cash: number; stocksValue: number; equity: number }>;
        }>)[accId];
        if (!run) {
          // eslint-disable-next-line no-console
          console.log(`[whb-inspect] no run for "${accId}". Known:`, Object.keys(g.__whbReconRuns ?? {}));
          return;
        }
        const acts = run.inputs.activities.filter((a) =>
          String(a.timestamp).slice(0, 10) === date
        );
        const ords = run.inputs.orders.filter((o) =>
          String(o.submittedAt).slice(0, 10) === date
        );
        const dayRow = run.days.find((d) => d.date === date);
        const prevIdx = run.days.findIndex((d) => d.date === date) - 1;
        const prevRow = prevIdx >= 0 ? run.days[prevIdx] : undefined;
        const cashChange =
          dayRow && prevRow ? dayRow.cash - prevRow.cash : dayRow?.cash ?? 0;
        const actSum = acts.reduce((t, a) => t + (a.amount ?? 0), 0);
        const orderCashImpact = ords.reduce((t, o) => {
          const tot = o.totalAmount ?? 0;
          return t + (o.side === "buy" ? -tot : tot);
        }, 0);
        // eslint-disable-next-line no-console
        console.group(`[whb-inspect ${accId} ${date}]`);
        // eslint-disable-next-line no-console
        console.log(`reconstructed cash ${date}: $${dayRow?.cash.toFixed(2) ?? "—"}`);
        // eslint-disable-next-line no-console
        console.log(`day-over-day cash change: $${cashChange.toFixed(2)}`);
        // eslint-disable-next-line no-console
        console.log(`activity total: $${actSum.toFixed(2)} (${acts.length} rows)`);
        // eslint-disable-next-line no-console
        console.table(
          acts.map((a) => ({ id: a.id, name: a.name, amount: a.amount, timestamp: a.timestamp }))
        );
        // eslint-disable-next-line no-console
        console.log(`order cash impact: $${orderCashImpact.toFixed(2)} (${ords.length} orders)`);
        // eslint-disable-next-line no-console
        console.table(
          ords.map((o) => ({
            id: o.id,
            side: o.side,
            asset: o.asset,
            qty: o.filledQty,
            price: o.avgFillPrice,
            total: o.totalAmount,
            qtyTimesPrice: +(o.filledQty * o.avgFillPrice).toFixed(4),
            diff: +(o.totalAmount - o.filledQty * o.avgFillPrice).toFixed(4),
            submittedAt: o.submittedAt,
          }))
        );
        // eslint-disable-next-line no-console
        console.log(
          `expected cash change = activity + order impact = $${(actSum + orderCashImpact).toFixed(2)}`
        );
        // eslint-disable-next-line no-console
        console.groupEnd();
      };
    }
    // eslint-disable-next-line no-console
    console.log(
      `[whb-reconstruct ${raw.id.slice(0, 8)}] orders=${s.ordersCount} ` +
        `cashflows=${s.cashflowsCount} tickers=${s.uniqueTickers} ` +
        `quotes=${s.tickersWithQuotes} missing=${miss.length} ` +
        `splits=${s.splitsApplied} positions=${s.positionsOpen} ` +
        `(market=${s.positionsMarketMarked} costBasis=${s.positionsCostBasisMarked}) ` +
        `final=$${s.finalEquity.toLocaleString()} ` +
        `(cash=$${s.finalCash.toLocaleString()} stocks=$${s.finalStocksValue.toLocaleString()}) ` +
        `window=${s.firstDate}→${s.lastDate}` +
        (srcSummary ? ` | sources: ${srcSummary}` : "") +
        (actSummary ? ` | activities by type: ${actSummary}` : "") +
        (miss.length > 0 ? ` | missing tickers: ${miss.slice(0, 20).join(",")}${miss.length > 20 ? "…" : ""}` : "")
    );
    // eslint-disable-next-line no-console
    console.log(
      `[whb-reconstruct ${raw.id.slice(0, 8)}] last-7-days: ${dailyTrail}`
    );
    // eslint-disable-next-line no-console
    console.log(
      `[whb-reconstruct ${raw.id.slice(0, 8)}] inspect: __whbInspectDay("${raw.id.slice(0, 8)}", "YYYY-MM-DD")`
    );
  }

  // Sergey 2026-05-01 (revert): use pure reconstruction with the
  // SGOV-gap shift applied above. The shift anchors today's value to
  // Alpaca's portfolio.cash and the rest of the curve carries the
  // same offset, matching Alpaca's daily values within the shift
  // constant. WHB's states[] use a different convention (likely
  // excluding margin liability) and don't match Alpaca, so the
  // earlier experiment of preferring states post-Sep-15 produced
  // curves the user couldn't reconcile. Restored to the original
  // recon-only path that the user said was correct.
  if (reconstructed && reconstructed.length > 1) {
    // SGOV-as-cash reclassification (future-proofing). When WHB does
    // start returning SGOV in raw.stocks[], this kicks in and moves
    // SGOV from stocks-side to cash-side of the reconstruction. Today
    // `cashEquivalentMV` is 0 because WHB filters SGOV from stocks[]
    // too, so this block is a no-op. Keep it so the fix lands cleanly
    // when the backend gets SGOV support.
    if (cashEquivalentMV > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[whb-reconstruct ${raw.id.slice(0, 8)}] SGOV-as-cash reclass: shift $${cashEquivalentMV.toFixed(2)} from cash to stocks on every day`
      );
      for (let i = 0; i < reconstructed.length; i++) {
        const d = reconstructed[i]!;
        reconstructed[i] = {
          ...d,
          cash: +(d.cash - cashEquivalentMV).toFixed(2),
          stocksValue: +(d.stocksValue + cashEquivalentMV).toFixed(2),
          equity: d.equity,
        };
      }
    }

    // Note: the portfolio-cash anchor is now applied ABOVE via the
    // SGOV-gap correction — the shift happens before the console log
    // block, using the persisted correction's amount. See
    // `upsertSgovGapCorrection` and the synthetic Order that lands in
    // the Orders tab.

    // Trading-day filter. The reconstruction emits one point per
    // calendar day, but weekends + NYSE holidays render as flat
    // ledges that dilute the chart's visual signal. Strip them here
    // so the chart only plots days the US equity market was open.
    //
    // Sergey 2026-04-26: previously kept the last row unconditionally
    // ("so the hero and right edge agree on weekends"), but that
    // surfaced Sat/Sun bars at the chart's right edge during
    // weekends — exactly what we set out to suppress. The hero number
    // doesn't change on closed days (equity = Friday's close all
    // weekend), so a chart whose right edge is Friday agrees with the
    // hero just as well. Strict filter from here on.
    const tradingDays = reconstructed.filter((d) => isTradingDay(d.date));

    // ── Alpaca-first splice (Sergey 2026-08-03) ──────────────────────
    //
    // `states[]` are per-day cash+stocks snapshots that WHB pulls from
    // Alpaca — i.e. Alpaca's own view of the account on that day. The
    // reconstruction, by contrast, rebuilds history from WHB's
    // `/trading-history` + activities, so it is blind to anything that
    // happened before the account was connected to WHB (or to any
    // activity the feed truncates).
    //
    // On Sergey Live *a110 that blindness is severe: Alpaca shows
    // ~$80k in Aug 2025 where the reconstruction shows ~$2.6k — a
    // ~$77k hole that made every derived metric nonsense (1Y read
    // "+4006%" simply because it divided by a $2.6k base).
    //
    // So: trust Alpaca (`states[]`) for every day it covers, and use
    // the reconstruction only OUTSIDE that window — before the first
    // state (deep history WHB can't see either way) and after the last
    // state (today's live tail, shifted to join the last state so the
    // splice doesn't introduce a step of its own).
    //
    // Flip `USE_ALPACA_STATES_CURVE` to false to restore the pure
    // reconstruction path.
    const USE_ALPACA_STATES_CURVE = true;
    const stateEquityByDate = new Map<string, number>();
    for (let i = 0; i < stateDates.length; i++) {
      const d = stateDates[i];
      const eq = stateEquity[i];
      if (typeof d === "string" && typeof eq === "number" && Number.isFinite(eq)) {
        stateEquityByDate.set(d, eq);
      }
    }
    const firstStateDate = stateDates.length > 0 ? stateDates[0]! : null;
    const lastStateDate =
      stateDates.length > 0 ? stateDates[stateDates.length - 1]! : null;

    if (USE_ALPACA_STATES_CURVE && stateEquityByDate.size > 0 && firstStateDate && lastStateDate) {
      // Verification pass — how far apart are the two sources on the
      // days they BOTH cover? A large mean gap means the
      // reconstruction inputs are incomplete and is worth surfacing,
      // since it also tells us how much the pre-first-state segment is
      // likely understated.
      let overlap = 0;
      let sumAbsDiff = 0;
      let maxAbsDiff = 0;
      let lastOverlapDiff = 0;
      for (const d of tradingDays) {
        const stateEq = stateEquityByDate.get(d.date);
        if (stateEq === undefined) continue;
        const diff = stateEq - d.equity;
        overlap++;
        sumAbsDiff += Math.abs(diff);
        if (Math.abs(diff) > Math.abs(maxAbsDiff)) maxAbsDiff = diff;
        lastOverlapDiff = diff;
      }
      if (typeof window !== "undefined" && overlap > 0) {
        // eslint-disable-next-line no-console
        console.log(
          `[whb-reconstruct ${raw.id.slice(0, 8)}] Alpaca states vs reconstruction: ${overlap} overlapping days, mean |Δ| $${(sumAbsDiff / overlap).toFixed(2)}, max Δ $${maxAbsDiff.toFixed(2)}, latest Δ $${lastOverlapDiff.toFixed(2)} — using Alpaca for ${firstStateDate}…${lastStateDate}`
        );
      }

      // Tail shift: reconstruction days AFTER the last state get
      // offset by the gap measured on the final overlapping day, so
      // "today" continues smoothly from Alpaca's last known value
      // instead of dropping back to the reconstruction's level.
      const tailShift = overlap > 0 ? lastOverlapDiff : 0;

      const splicedEquity: number[] = [];
      const splicedDates: string[] = [];
      for (const d of tradingDays) {
        const stateEq = stateEquityByDate.get(d.date);
        if (stateEq !== undefined) {
          splicedEquity.push(+stateEq.toFixed(2));
        } else if (d.date > lastStateDate) {
          splicedEquity.push(+(d.equity + tailShift).toFixed(2));
        } else {
          // Before the first state (or a state gap) — reconstruction is
          // all we have. Left un-shifted: a flat offset would be a lie
          // about the deep past, where the account genuinely started
          // near zero.
          splicedEquity.push(+d.equity.toFixed(2));
        }
        splicedDates.push(d.date);
      }
      equityCurve = splicedEquity;
      curveDates = splicedDates;
    } else {
      equityCurve = tradingDays.map((d) => d.equity);
      curveDates = tradingDays.map((d) => d.date);
    }
  } else {
    // Legacy path: pre-state backfill prepended to states.
    const preState =
      cashflows && stateDates.length > 0
        ? reconstructPreStateCurve(cashflows, stateDates[0])
        : null;
    const rawEquity = preState
      ? [...preState.equity, ...stateEquity]
      : stateEquity;
    const rawDates = preState
      ? [...preState.dates, ...stateDates]
      : stateDates;
    // Trading-day filter — mirrors the reconstruction branch above so
    // every chart-feeding path drops weekends + NYSE holidays. Strict
    // filter (Sergey 2026-04-26): the previous "always keep last row"
    // exception leaked Sat/Sun bars onto the right edge during
    // weekends. Equity doesn't change on closed days, so a chart
    // ending Friday is the right answer — the hero pulls the same
    // last-known value either way.
    const filteredEquity: number[] = [];
    const filteredDates: string[] = [];
    for (let i = 0; i < rawDates.length; i++) {
      const d = rawDates[i]!;
      if (isTradingDay(d)) {
        filteredEquity.push(rawEquity[i] ?? 0);
        filteredDates.push(d);
      }
    }
    equityCurve = filteredEquity;
    curveDates = filteredDates;
  }

  // Build the portfolio TWR curve. Two paths:
  //
  //   1. Cashflows present → chain-linked TWR (industry standard).
  //      The naive `(equity[i] / equity[0] − 1)` formula double-
  //      counts user deposits: a $10k deposit mid-period shows up
  //      as a fake +10k "return". Chain-linking neutralizes that
  //      by computing per-period returns between cashflow events.
  //
  //   2. No cashflows → fall back to the legacy naive path via
  //      `buildAccountRiskData`. Correct when there are no inflows
  //      to bleed in (demo accounts, freshly opened real accounts).
  //
  // Either way we feed the result through `buildRiskDataForTWR`
  // which adds the synthetic SPY benchmark + computes every risk
  // metric (Sharpe, MaxDD, APY, β, α, Calmar, vol). The SPY side
  // is still synthesized (seeded by the account id); switch to a
  // real SPY price series later without touching this function —
  // just thread it in through the helper.
  const riskSeed = hashStringToSeed(raw.id);
  // Only pass real SPY when we also have real `curveDates` of matching
  // length — the helper aligns prices to dates, so without dates it
  // falls back to the synthetic walk. `spyPrices` is cross-account
  // shared data from `useRealSpyPriceSeries`.
  const realSpyOpts =
    spyPrices && spyPrices.length > 0
      ? { curveDates, spyPrices }
      : undefined;
  let risk: ReturnType<typeof buildAccountRiskData> | null = null;
  // `twrCurve` exposed in this scope so the all-time %/$ overlay
  // below can read its last value — that's the chain-linked total
  // return that doesn't double-count deposits/withdrawals as
  // gains/losses (the bug Sergey caught 2026-04-30: an account that
  // dropped from $60k to $70 entirely via withdrawals was reading
  // −99.65% instead of the small real-market drift it actually had).
  let twrCurve: number[] | null = null;
  if (equityCurve.length > 1) {
    if (cashflows && cashflows.length > 0) {
      twrCurve = computeChainLinkedTWR(equityCurve, curveDates, cashflows);
      risk = buildRiskDataForTWR(twrCurve, riskSeed, realSpyOpts);
    } else {
      // No cashflows → naive price-ratio TWR still works (demo
      // accounts and brand-new real accounts with no deposits yet).
      // Even here we prefer real SPY when available so the
      // comparison line reflects the actual market.
      const start = equityCurve[0] ?? 1;
      twrCurve = equityCurve.map(
        (v) => +(((v / start) - 1) * 100).toFixed(3)
      );
      risk = buildRiskDataForTWR(twrCurve, riskSeed, realSpyOpts);
    }
  }

  const stocks = Array.isArray(raw.stocks) ? raw.stocks : [];
  const bots = Array.isArray(raw.bots) ? raw.bots : [];

  const nonCashEquivalentStocks = stocks.filter(
    (s) => s && !isCashEquivalent(s.symbol)
  );

  const equity = typeof portfolio.equity === "number" ? portfolio.equity : 0;
  const buyingPower =
    typeof portfolio.buyingPower === "number" ? portfolio.buyingPower : 0;
  const stocksValue =
    typeof portfolio.stocksValue === "number" ? portfolio.stocksValue : 0;
  const cashValue =
    typeof portfolio.cash === "number" ? portfolio.cash : 0;
  const stocksPurchaseValue =
    typeof portfolio.stocksPurchaseValue === "number"
      ? portfolio.stocksPurchaseValue
      : 0;

  // ---- All-time return: TWR + ND-adjusted dollars ------------------------
  //
  // Sergey 2026-04-30: an account that was funded $60k, withdrew
  // $41k over the year, and now sits at $70 was reading −99.65%
  // because the prior implementation pulled `portfolio.stocksProfitPercents`
  // straight from the broker — and that field treats the withdrawn
  // cash as a loss. Replace with a chain-linked TWR (which the rest
  // of the risk bundle already uses) so the headline tracks real
  // market-driven performance and ignores cashflows.
  //
  //   • allTimeChangePct  ← lastValue(twrCurve)
  //                          (already a percentage in the same unit
  //                           the rest of the app expects).
  //   • allTimeChange ($) ← currentEquity − Σ cashflows.
  //                          Σ cashflows = net deposits (deposits
  //                          positive, withdrawals negative). The
  //                          dollar return is "money in the account
  //                          today minus money the user pushed in
  //                          (or pulled out)". Demo / real both fall
  //                          back to the broker's `totalProfit` /
  //                          stocksValue−cost-basis when no cashflows
  //                          are available so demo accounts keep
  //                          working.
  const sumCashflows =
    cashflows && cashflows.length > 0
      ? cashflows.reduce((s, cf) => s + (Number.isFinite(cf.amount) ? cf.amount : 0), 0)
      : 0;
  // Sergey 2026-05-01: switched allTimeChangePct from
  // `lastValue(twrCurve)` to direct money-weighted return because the
  // chain-linked TWR was getting distorted whenever the equity curve
  // had any synthetic stitching (recon→states boundary, anchor
  // adjustments, etc.) — small per-day biases compound over hundreds
  // of days and can flip the sign of the result. The simple
  // (equity − Σ cashflows) / Σ cashflows formula always agrees with
  // the displayed dollar change above because both use the same
  // numerator and denominator. For Ellen Live: ($59,015 − $50,000) /
  // $50,000 = +18.03 % (matches Alpaca exactly). Net deposits-based
  // return is also what most brokers (Alpaca included) display as
  // "All time" %.
  const allTimeChange =
    cashflows && cashflows.length > 0
      ? equity - sumCashflows
      : typeof portfolio.totalProfit === "number" && portfolio.totalProfit !== 0
        ? portfolio.totalProfit
        : stocksValue - stocksPurchaseValue;
  const allTimeChangePct =
    cashflows && cashflows.length > 0 && sumCashflows > 0
      ? allTimeChange / sumCashflows
      : typeof portfolio.stocksProfitPercents === "number"
        ? portfolio.stocksProfitPercents
        : 0;

  const botIds = bots
    .map((b) => b?.id)
    .filter((x): x is string => typeof x === "string" && x.length > 0);

  // Note: `cashflows` is computed earlier (above the risk calculation)
  // so chain-linked TWR can use it. The overlay write at the bottom
  // of this function just copies the same value through.

  // Overlay only. Omit identity fields (name/provider/masked/paper)
  // so the caller's spread preserves the list-row values.
  const overlay: Partial<BrokerAccount> = {
    id: raw.id,
    // Real bot count when the detail endpoint gives us the array.
    botsCount:
      typeof raw.botsCount === "number" && raw.botsCount > 0
        ? raw.botsCount
        : bots.length,

    portfolioValue: equity,
    buyingPower,
    buyingPowerExBots: buyingPower,
    // portfolio.cash and portfolio.stocksValue come from WHB's
    // /broker-accounts endpoint, which already matches Alpaca's
    // displayed cash/stocks split (SGOV is part of whichever bucket
    // Alpaca puts it in). Don't reclassify — that would un-align from
    // Alpaca's own numbers. Only the reconstruction needs SGOV help.
    stocksValue,
    cashValue,
    stocksCount: nonCashEquivalentStocks.length,
    stocksDayChangePct: 0,
    allTimeChange,
    allTimeChangePct,

    equityCurve,
    curveDates,
    // Real accounts use the "total-equity" basis: each point in
    // equityCurve is the actual cash+stocks balance on that day
    // (state period) or cumulative net deposits (pre-state backfill).
    // The chart's layering formula must NOT add cashflows on top or
    // it would double-count — the deposits are already reflected.
    portfolioBasis: "total-equity",

    botIds,
  };
  // TWR / SPY / risk — synthesized from equityCurve above. For very
  // short series (≤1 point) the math would divide by zero; skip the
  // override entirely so the list-row's empty arrays + EMPTY_RISK
  // stay in place (chart will still render the portfolio line).
  if (risk) {
    overlay.twrCurve = risk.twrCurve;
    overlay.spyCurve = risk.spyCurve;
    overlay.spyTotalCurve = risk.spyTotalCurve;
    overlay.spyReturnPct = risk.spyReturnPct;
    overlay.risk = risk.risk;
  }
  if (cashflows) {
    overlay.cashflows = cashflows;
  }
  return overlay;
}
